import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAdminDashboardStats } from '../api';
import { applyVisualSelection, toggleVisualSelection } from '../utils/adminChartData';
import { getPresetRange } from '../utils/adminMetrics';
import AdminChartGrid from './AdminChartGrid';
import AdminFilters from './AdminFilters';
import AdminMetricsCards from './AdminMetricsCards';
import AdminTransactionTable from './AdminTransactionTable';

function AdminDashboard({ availableTMs = [] }) {
  const initialRange = useMemo(() => getPresetRange('90d'), []);
  const [activePreset, setActivePreset] = useState('90d');
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [userFilter, setUserFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('all');
  const [tmFilter, setTmFilter] = useState('ALL');
  const [languageFilter, setLanguageFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [visualSelection, setVisualSelection] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    setVisualSelection(null);
    try {
      const response = await getAdminDashboardStats({
        start: startDate,
        end: endDate,
        user: userFilter,
        type: typeFilter,
        tm_id: tmFilter === 'ALL' ? '' : tmFilter,
        targetLanguage: languageFilter,
      });
      setData(response);
    } catch (err) {
      setError(err.message || 'Failed to load admin dashboard.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, userFilter, typeFilter, tmFilter, languageFilter]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handlePresetChange = (presetId) => {
    const range = getPresetRange(presetId);
    setActivePreset(presetId);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const handleStartDateChange = (value) => {
    setActivePreset('custom');
    setStartDate(value);
  };

  const handleEndDateChange = (value) => {
    setActivePreset('custom');
    setEndDate(value);
  };

  const handleVisualSelect = (next) => {
    setVisualSelection((current) => toggleVisualSelection(current, next));
  };

  const dateRangeLabel = startDate === 'ALL'
    ? 'all-time'
    : `${startDate || 'start'}-to-${endDate || 'end'}`;

  const baseRows = useMemo(() => data?.transactions || [], [data]);
  const filteredRows = useMemo(
    () => applyVisualSelection(baseRows, visualSelection),
    [baseRows, visualSelection],
  );

  return (
    <div className="admin-dashboard">
      <AdminFilters
        activePreset={activePreset}
        startDate={startDate}
        endDate={endDate}
        userFilter={userFilter}
        typeFilter={typeFilter}
        tmFilter={tmFilter}
        languageFilter={languageFilter}
        availableTMs={availableTMs}
        users={data?.users || []}
        loading={loading}
        onPresetChange={handlePresetChange}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onUserChange={setUserFilter}
        onTypeChange={setTypeFilter}
        onTmChange={setTmFilter}
        onLanguageChange={setLanguageFilter}
        onRefresh={fetchDashboard}
      />

      {error && (
        <div className="message message-error">
          <span className="message-text">{error}</span>
        </div>
      )}

      {data?.truncated && (
        <div className="admin-truncated-warning">
          Results are truncated. Narrow the date range for a complete view.
        </div>
      )}

      <AdminMetricsCards summary={data?.summary} />

      <AdminChartGrid
        baseRows={baseRows}
        filteredRows={filteredRows}
        selection={visualSelection}
        onVisualSelect={handleVisualSelect}
        availableTMs={availableTMs}
      />

      <AdminTransactionTable
        transactions={filteredRows}
        loading={loading}
        dateRangeLabel={dateRangeLabel}
        availableTMs={availableTMs}
      />
    </div>
  );
}

export default AdminDashboard;
